import { animate, state, style, transition, trigger } from '@angular/animations';
import {
  Component,
  effect,
  ElementRef,
  inject,
  input,
  QueryList,
  signal,
  ViewChildren,
} from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ReactiveFormsModule } from '@angular/forms';
import { MatDialogModule } from '@angular/material/dialog';
import { CurrencyMaskModule } from 'ng2-currency-mask';
import { CardsModule } from '@app-components/cards/cards.module';
import { DialogUploadAudioReportItemComponent } from '@app-components/dialog/dialog-upload-audio-report-item/dialog-upload-audio-report-item.component';
import { DialogUploadReportItemComponent } from '@app-components/dialog/dialog-upload-report-item/dialog-upload-report-item.component';
import { Axle, TireReport } from '@app-model/evaluation';
import { EvaluationService } from '@app-services/evaluation.service';
import { StringUtils } from '@app-utils/StringUtils';

@Component({
  selector: 'app-report-item',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatDialogModule,
    CurrencyMaskModule,
    CardsModule,
  ],
  templateUrl: './report-item.component.html',
  styleUrls: ['./report-item.component.scss'],
  animations: [
    trigger('expandCollapse', [
      state('collapsed', style({ height: '0px', overflow: 'hidden', paddingTop: '0px' })),
      state('expanded', style({ height: '*', overflow: 'hidden', paddingTop: '20px', paddingLeft: '15px' })),
      transition('collapsed => expanded', [
        animate('0.3s ease-out', style({ height: '*', paddingTop: '20px', paddingLeft: '15px' })),
        animate('0.1s', style({ paddingTop: '20px' })),
      ]),
      transition('expanded => collapsed', [
        animate('0.3s ease-out', style({ height: '0px', paddingTop: '0px' })),
      ]),
    ]),
  ],
})
export class ReportItemComponent {
  private readonly dialog = inject(MatDialog);
  private readonly fb = inject(FormBuilder);
  private readonly evalService = inject(EvaluationService);

  vehicleId = input.required<string>();
  item = input.required<FormGroup>();
  index = input.required<number>();
  key = input.required<string>();
  axles = input<Axle[]>([]);
  tireReportsFormArray = input<FormArray | null>(null);
  serviceType = input<'evaluation' | 'oem'>('evaluation');
  required = input<boolean>(false);

  @ViewChildren('separator') separator!: QueryList<ElementRef>;

  readonly tiresByAxle = signal<Map<number, FormArray>>(new Map());
  readonly isMobile = signal(window.innerWidth < 576);
  readonly comments = signal<string>('');
  readonly opened = signal(false);
  readonly estimatedCost = signal<number | undefined>(undefined);
  readonly imgQty = signal(0);
  readonly videoQty = signal(0);
  readonly audioQty = signal(1);

  constructor() {
    effect(onCleanup => {
      const it = this.item();
      const tireReports = this.tireReportsFormArray();
      const keyVal = this.key();
      const axlesVal = this.axles();

      if (!it?.value) return;

      const value = it.value as ReportItemFormValue;
      if (this.isMobile() && value.name?.length > 9) {
        it.patchValue({ name: value.name.substring(0, 9) + '.' });
      }

      this.comments.set(value.description ?? '');
      this.estimatedCost.set(value.fixingCost);
      const urls = value.photosAndVideosUrls ?? [];
      this.imgQty.set(urls.filter((url: string) => !StringUtils.isVideoUrl(url)).length);
      this.videoQty.set(urls.filter((url: string) => StringUtils.isVideoUrl(url)).length);
      this.audioQty.set(value.audio ? 1 : 0);

      if (tireReports && keyVal === 'TIRES' && axlesVal?.length) {
        this.tiresByAxle.set(this.getTiresByAxle(axlesVal, tireReports));
        const subs: { unsubscribe: () => void }[] = [];
        tireReports.controls.forEach(control => {
          subs.push(
            control.valueChanges.subscribe(() => {
              this.updateTireClassification(it, tireReports);
            })
          );
        });
        subs.push(
          this.evalService.onAxleChanged().subscribe(() => {
            this.tiresByAxle.set(this.getTiresByAxle(axlesVal, tireReports, true));
          })
        );
        onCleanup(() => subs.forEach(s => s.unsubscribe()));
      }
    }, { allowSignalWrites: true });
  }

  private updateTireClassification(item: FormGroup, tireReportsFormArray: FormArray): void {
    const value = tireReportsFormArray.getRawValue();
    const hasBadTire = value.some((tire: TireReport) => tire.classification === 'BAD');
    if (hasBadTire) {
      item.patchValue({ classification: 'BAD' });
      return;
    }
    const hasRegularTire = value.some((tire: TireReport) => tire.classification === 'REGULAR');
    if (hasRegularTire) {
      item.patchValue({ classification: 'REGULAR' });
      return;
    }
    const hasGoodTire = value.some((tire: TireReport) => tire.classification === 'GOOD');
    if (hasGoodTire) {
      item.patchValue({ classification: 'GOOD' });
    }
  }

  private getTiresByAxle(
    axlesVal: Axle[],
    tireReportsFormArray: FormArray,
    force = false
  ): Map<number, FormArray> {
    if (tireReportsFormArray.value.length === 0 || force) {
      this.buildFormArray(axlesVal, tireReportsFormArray);
    }
    return this.buildTireReportMap(tireReportsFormArray);
  }

  private buildFormArray(axlesVal: Axle[], tireReportsFormArray: FormArray): void {
    axlesVal.forEach(axle => {
      for (let i = 0; i < axle.tireCount; i++) {
        const exists = tireReportsFormArray.controls.some(
          c => c.value.axlePosition === axle.position + 1 && c.value.tirePosition === i + 1
        );
        if (exists) continue;
        tireReportsFormArray.push(
          this.fb.group({
            tirePosition: i + 1,
            axlePosition: axle.position + 1,
            classification: 'NON_EXISTENT',
          })
        );
      }
    });
  }

  private buildTireReportMap(tireReportsFormArray: FormArray): Map<number, FormArray> {
    return tireReportsFormArray.controls.reduce(
      (acc, control) => {
        const axlePosition = control.value.axlePosition;
        if (!acc.has(axlePosition)) {
          acc.set(axlePosition, this.fb.array([]));
        }
        acc.get(axlePosition)!.push(control);
        return acc;
      },
      new Map<number, FormArray>()
    );
  }

  onAnimationDone(event: { toState: string }): void {
    if (event.toState === 'collapsed' && this.separator?.first) {
      this.separator.first.nativeElement.style.display = 'block';
    }
  }

  onAnimationStart(event: { toState: string }): void {
    if (event.toState === 'expanded' && this.separator?.first) {
      this.separator.first.nativeElement.style.display = 'none';
    }
  }

  selectOption(option: 'GOOD' | 'REGULAR' | 'BAD'): void {
    const current = this.item().get('classification')?.value;
    if (current === option) {
      this.item().patchValue({ classification: 'NON_EXISTENT' });
      this.opened.set(false);
      return;
    }
    this.item().patchValue({ classification: option });
    this.opened.set(option === 'BAD' || option === 'REGULAR');
  }

  toggle(): void {
    this.opened.update(v => !v);
  }

  onBlur(): void {
    this.item().patchValue({ description: this.comments() });
    this.item().patchValue({ fixingCost: this.estimatedCost() });
  }

  openModalUploadPhoto(mediaType: 'photo' | 'video' = 'photo'): void {
    const currentUrls: string[] = this.item().value.photosAndVideosUrls ?? [];
    const dialogRef = this.dialog.open(DialogUploadReportItemComponent, {
      disableClose: false,
      width: '635px',
      hasBackdrop: true,
      panelClass: 'dialog-upload-report-item',
      data: {
        title: this.item().value.name,
        vehicleId: this.vehicleId(),
        photosAndVideos: currentUrls,
        ReportEquipment: this.key(),
        serviceType: this.serviceType(),
        mediaType,
      },
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result?.photosAndVideos) {
        const photos = currentUrls.filter((url: string) => !StringUtils.isVideoUrl(url));
        const videos = currentUrls.filter((url: string) => StringUtils.isVideoUrl(url));
        const fromDialog = result.photosAndVideos as string[];
        const fromDialogMarked =
          mediaType === 'video'
            ? fromDialog.map((url: string) =>
                StringUtils.isVideoUrl(url) ? url : url + '#video'
              )
            : fromDialog;
        const merged =
          mediaType === 'photo'
            ? [...fromDialogMarked, ...videos]
            : [...photos, ...fromDialogMarked];
        this.item().patchValue({ photosAndVideosUrls: merged });
        this.imgQty.set(merged.filter((url: string) => !StringUtils.isVideoUrl(url)).length);
        this.videoQty.set(merged.filter((url: string) => StringUtils.isVideoUrl(url)).length);
      }
    });
  }

  openModalUploadAudio(): void {
    const dialogRef = this.dialog.open(DialogUploadAudioReportItemComponent, {
      width: '635px',
      disableClose: false,
      hasBackdrop: true,
      panelClass: 'dialog-upload-report-item',
      data: {
        title: this.item().value.name,
        vehicleId: this.vehicleId(),
        audio: this.item().value.audio,
        serviceType: this.serviceType(),
        equipmentKey: this.key(),
      },
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result?.remove) {
        this.item().patchValue({ audio: null });
        this.audioQty.set(0);
      } else if (result?.url) {
        this.item().patchValue({ audio: result });
        this.audioQty.set(1);
      }
    });
  }
}

interface ReportItemFormValue {
  name?: string;
  classification?: string;
  description?: string;
  fixingCost?: number;
  photosAndVideosUrls?: string[];
  audio?: unknown;
}
